import React, { useState, useEffect } from 'react'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import {
  Btn,
  CustomDropdown,
  CustomModal,
  InputPhone,
  CustomInputTextArea,
  SectionTitle,
} from '../../../../../components'
import { getEmissoresByTps } from '../../../../../services/api/https/settings/emissor-cte.http'
import {
  postEmitCTeComplementar,
  getRegrasFreteCteComplementar,
} from '../../../../../services/api/https/deliveries/cte.http'
import { getCfops } from '../../../../../services/api/https/settings/cfop.http'
import { somenteDigitos } from '../../../../../services/util/documento.service'
import ValoresFiscaisCte from '../ValoresFiscaisCte/ValoresFiscaisCte'
import {
  ERROS_VALORES_FISCAIS,
  VALORES_FISCAIS_INICIAIS,
  aplicarTotal,
  erroValoresFiscais,
  montarComponentesValor,
  novoComponente,
  somaComponentes,
  toNum,
} from '../ValoresFiscaisCte/valoresFiscaisCte.util'

function formatarValor(v) {
  const n = toNum(v)
  return n != null ? n.toFixed(2).replace('.', ',') : ''
}

const inicialState = {
  emissor_id: null,
  cfop_id: null,
  ...VALORES_FISCAIS_INICIAIS,
  observacoes: null,
}

const errors = {
  emissor_id: 'O campo Emissor é obrigatório',
  cfop_id: 'O campo CFOP é obrigatório',
  ...ERROS_VALORES_FISCAIS,
}

export default function ModalEmitirCteComplementar({
  selectedTp,
  visible,
  cteComplementado,
  onModalClose,
  onModalSave,
  onError,
}) {
  // Hooks
  const [isLoading, setIsLoading] = useState(false)
  const [state, setState] = useState(inicialState)
  const [emissores, setEmissores] = useState([])
  const [cfops, setCfops] = useState([])
  const [componentes, setComponentes] = useState([])
  const [faixaKmName, setFaixaKmName] = useState(null)
  const [regrasFaixa, setRegrasFaixa] = useState([])
  const [showRegrasModal, setShowRegrasModal] = useState(false)
  const [avisosRegras, setAvisosRegras] = useState([])
  const [telefoneTomador, setTelefoneTomador] = useState('')

  const isSubcontratacao = Boolean(cteComplementado && cteComplementado.document_origin === 'cte')

  useEffect(() => {
    // O modal fica montado entre aberturas: uma resposta atrasada de uma abertura anterior não
    // pode sobrescrever os dados do CTe atual
    let aberturaAtiva = true

    // Regras de Frete (tela 2.15) das notas do CTe complementado, para compor os componentes
    async function fetchRegrasComplementar() {
      try {
        const dados = await getRegrasFreteCteComplementar(cteComplementado.id)
        if (!aberturaAtiva) return
        setFaixaKmName(dados.faixa_km_name)
        setRegrasFaixa(dados.regras || [])
        setAvisosRegras(dados.avisos || [])
      } catch (error) {
        console.error('Erro ao buscar regras de frete do CTe complementado:', error)
        if (aberturaAtiva) setAvisosRegras([String((error && error.message) || error)])
      }
    }

    if (!visible || !cteComplementado) return undefined
    // O complementar parte zerado: só os componentes informados compõem o valor. Emissor, CFOP,
    // situações tributárias e alíquotas vêm do CTe complementado (a SEFAZ exige o mesmo emitente
    // e o mesmo CST); o modal é reaberto para CTes diferentes, então nada do anterior é mantido.
    setComponentes([])
    setFaixaKmName(null)
    setRegrasFaixa([])
    setAvisosRegras([])
    setShowRegrasModal(false)
    setTelefoneTomador(cteComplementado.cte_origem_telefone || '')
    setState({
      ...inicialState,
      emissor_id: cteComplementado.emissor_id,
      cfop_id: cteComplementado.cfop_id,
      icms_situacao_tributaria: cteComplementado.icms_situacao_tributaria || null,
      icms_aliquota: toNum(cteComplementado.icms_aliquota),
      ibs_cbs_situacao_tributaria: cteComplementado.ibs_cbs_situacao_tributaria || null,
      ibs_cbs_classificacao_tributaria: cteComplementado.ibs_cbs_classificacao_tributaria || null,
      ibs_uf_aliquota: toNum(cteComplementado.ibs_uf_aliquota),
      ibs_mun_aliquota: toNum(cteComplementado.ibs_mun_aliquota),
      cbs_aliquota: toNum(cteComplementado.cbs_aliquota),
      observacoes: `CT-e complementar ao CT-e nº ${cteComplementado.numero} série ${cteComplementado.serie}. Motivo: `,
    })
    fetchRegrasComplementar()
    return () => {
      aberturaAtiva = false
    }
  }, [cteComplementado, visible])

  useEffect(() => {
    async function fetchData() {
      try {
        const [listaEmissores, listaCfops] = await Promise.all([
          getEmissoresByTps(selectedTp),
          getCfops(),
        ])
        setEmissores(listaEmissores)
        setCfops(
          listaCfops.map(cfop => ({
            ...cfop,
            codigoNatureza: `${cfop.codigo} - ${cfop.natureza}`,
          })),
        )
      } catch (error) {
        onError(error)
      }
    }
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTp])

  if (!cteComplementado) return null

  const emissorSelecionado = emissores.find(item => item.id === state.emissor_id) || null
  const cfopSelecionado = cfops.find(item => item.id === state.cfop_id) || null

  // A Regra de Frete escolhida vira um componente do valor e entra no total do frete
  const onRegraSelecionada = regra => {
    const novos = [...componentes, novoComponente(regra.nome, regra.valor)]
    setComponentes(novos)
    setState(prevState => aplicarTotal(prevState, somaComponentes(novos)))
    setShowRegrasModal(false)
  }

  // Events
  const onBtnSalvarClicked = async () => {
    try {
      Object.keys(state).forEach(key => {
        if (key !== 'observacoes' && state[key] === null) {
          throw new Error(errors[key])
        }
      })
      if (state.observacoes && state.observacoes.length > 2000) {
        throw new Error('O campo Observações deve ter no máximo 2000 caracteres')
      }
      const erroFiscal = erroValoresFiscais(state)
      if (erroFiscal) throw new Error(erroFiscal)

      const componentesValor = montarComponentesValor(componentes)
      if (!componentesValor.length) {
        throw new Error('Adicione ao menos um Componente do Valor para o CTe Complementar')
      }
      if (isSubcontratacao) {
        const telefone = somenteDigitos(telefoneTomador)
        if (telefone.length < 6 || telefone.length > 14) {
          throw new Error(
            'Informe o Telefone do Tomador (emitente do CTe de origem) com 6 a 14 dígitos',
          )
        }
      }

      setIsLoading(true)
      await postEmitCTeComplementar({
        ...state,
        cte_id: cteComplementado.id,
        componentes_valor: componentesValor,
        ...(isSubcontratacao ? { telefone_tomador: somenteDigitos(telefoneTomador) } : {}),
      })
      onModalSave(cteComplementado.id)
    } catch (error) {
      onError(error)
    } finally {
      setIsLoading(false)
    }
  }

  // Documento de referência do CTe complementado, conforme a origem documental dele
  const renderDocumentoComplementado = () => {
    if (isSubcontratacao) {
      return (
        <div>
          <b>CT-e de origem:</b> nº {cteComplementado.cte_origem_numero} de{' '}
          {cteComplementado.cte_origem_emitente}
          <span style={{ color: 'gray' }}> (CTe de Subcontratação)</span>
        </div>
      )
    }
    if (cteComplementado.document_origin === 'outros') {
      return (
        <div>
          <b>Documento(s):</b> {cteComplementado.documento_descricao || '-'}
          <span style={{ color: 'gray' }}> (CTe sem Nota Fiscal)</span>
        </div>
      )
    }
    return (
      <div>
        <b>Nota(s) Fiscal(is):</b> {cteComplementado.identifier}
      </div>
    )
  }

  // Render
  return (
    <CustomModal
      isVisible={visible}
      header={'Emitir CTe Complementar'}
      width={800}
      onClose={onModalClose}
      contentStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
    >
      <form className="p-grid" onSubmit={e => e.preventDefault()}>
        <div className="p-col-12">
          <SectionTitle title="CTe Complementado" />
          <div style={{ fontSize: 13, lineHeight: 1.7 }}>
            <div>
              <b>CTe:</b> nº {cteComplementado.numero} - série {cteComplementado.serie}
              {cteComplementado.globalizado ? ' (globalizado)' : ''}
            </div>
            <div>
              <b>Chave:</b> {cteComplementado.chave}
            </div>
            <div>
              <b>Cliente:</b> {cteComplementado.client_name}
              {cteComplementado.client_city ? ` - ${cteComplementado.client_city}` : ''}
            </div>
            <div>
              <b>Empresa:</b> {cteComplementado.company_name}
            </div>
            {renderDocumentoComplementado()}
            <div>
              <b>Valor do frete original:</b> R$ {formatarValor(cteComplementado.valor_total)}
            </div>
          </div>
          <span style={{ color: 'gray', fontSize: 12 }}>
            O CTe Complementar repete emissor, remetente, destinatário e tomador do CTe original;
            informe apenas os valores a complementar.
          </span>
        </div>
        <div className="p-md-6 p-col-12">
          <CustomDropdown
            optionLabel="razao_social"
            options={emissores}
            value={emissorSelecionado}
            label="Emissor:*"
            filter={true}
            disabled={true}
            onChange={() => {}}
          />
        </div>
        <div className="p-md-6 p-col-12">
          <CustomDropdown
            optionLabel="codigoNatureza"
            options={cfops}
            value={cfopSelecionado}
            label="CFOP:*"
            filter={true}
            onChange={value => setState(prevState => ({ ...prevState, cfop_id: value.id }))}
            tooltip={'Validado pelas UFs do CTe complementado e do emissor (matriz da SEFAZ)'}
          />
        </div>
        {isSubcontratacao && (
          <div className="p-md-6 p-col-12">
            <InputPhone
              label="Telefone do tomador:*"
              marginTop={10}
              mask="(99)9999-9999?9"
              autoClear={false}
              value={telefoneTomador}
              onChange={value => setTelefoneTomador(value || '')}
            />
            <span style={{ color: 'gray', fontSize: 12 }}>
              Emitente do CTe de origem, tomador do CTe de Subcontratação
            </span>
          </div>
        )}

        <ValoresFiscaisCte
          key={visible ? 'aberto' : 'fechado'}
          state={state}
          onStateChange={setState}
          componentes={componentes}
          onComponentesChange={setComponentes}
          onError={onError}
          componentesEmptyMessage="Nenhum componente. Adicione um Novo Campo ou uma Regra de Frete com o valor a complementar."
          componentesActions={
            <Btn onClick={() => setShowRegrasModal(true)}>Nova Regra de Frete</Btn>
          }
        />

        <div className="p-col-12">
          <CustomInputTextArea
            label="Observações"
            marginTop={10}
            rows={4}
            value={state.observacoes || ''}
            onChange={value => setState({ ...state, observacoes: value })}
          />
        </div>

        <div className="p-col-12" style={{ display: 'flex', justifyContent: 'end' }}>
          <Btn onClick={onBtnSalvarClicked} isLoading={isLoading}>
            Salvar
          </Btn>
        </div>
      </form>

      {/* Regras de Frete da Faixa de KM das notas do CTe complementado */}
      <CustomModal
        isVisible={showRegrasModal}
        header={`Regras de Frete${faixaKmName ? ` - ${faixaKmName}` : ''}`}
        width={600}
        onClose={() => setShowRegrasModal(false)}
      >
        <span style={{ color: 'gray', fontSize: 12 }}>
          Clique na regra desejada para adicioná-la aos Componentes do Valor
        </span>
        {avisosRegras.map((aviso, index) => (
          <div key={index} style={{ color: '#e67e22', fontSize: 12, marginTop: 5 }}>
            {aviso}
          </div>
        ))}
        <DataTable
          value={regrasFaixa}
          selectionMode="single"
          onRowClick={e => onRegraSelecionada(e.data)}
          emptyMessage={
            avisosRegras.length
              ? 'Nenhuma regra de frete disponível para este CTe'
              : 'Nenhuma regra de frete cadastrada para a faixa de KM dessa cidade'
          }
          style={{ marginTop: 10 }}
        >
          <Column field="nome" header="Regra de Faturamento" />
          <Column field="tipo" header="Tipo" style={{ width: 140 }} />
          <Column
            field="valor"
            header="Valor"
            style={{ width: 140 }}
            body={r => formatarValor(r.valor)}
          />
        </DataTable>
      </CustomModal>
    </CustomModal>
  )
}
